
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { QuickscanData, QuickscanResults } from '@/types/quickscan';

export const usePDFGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const generateClientSidePDF = (
    htmlTemplate: string,
    quickscanData: QuickscanData,
    results: QuickscanResults,
    contactInfo?: {
      email?: string;
      companyName?: string;
    },
    submissionId?: string
  ) => {
    console.log('Fallback: Generating PDF client-side');
    
    // Create a new window with the HTML content for printing/PDF
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Popup blocker verhindert PDF generatie. Schakel popup blocker uit.');
    }
    
    // Process the HTML template with data
    let processedHtml = htmlTemplate;
    
    // Replace placeholders (same logic as server)
    processedHtml = processedHtml.replace(/{{businessType}}/g, quickscanData.businessType || 'Niet opgegeven');
    processedHtml = processedHtml.replace(/{{employeeCount}}/g, quickscanData.employeeCount || 'Niet opgegeven');
    processedHtml = processedHtml.replace(/{{monthlyEnergyCosts}}/g, quickscanData.monthlyEnergyCosts?.toString() || '0');
    processedHtml = processedHtml.replace(/{{estimatedPvGeneration}}/g, quickscanData.estimatedPvGeneration?.toString() || '0');
    processedHtml = processedHtml.replace(/{{estimatedKwhUsage}}/g, quickscanData.estimatedKwhUsage?.toString() || '0');
    processedHtml = processedHtml.replace(/{{connectionCapacityDelivery}}/g, quickscanData.connectionCapacityDelivery?.toString() || '0');
    processedHtml = processedHtml.replace(/{{connectionCapacityReturn}}/g, quickscanData.connectionCapacityReturn?.toString() || '0');
    
    const peakHours = quickscanData.energyPeakHours?.map((hour: number) => `${hour}:00`).join(', ') || 'Geen gespecificeerd';
    processedHtml = processedHtml.replace(/{{energyPeakHours}}/g, peakHours);
    
    const solutions = quickscanData.currentSolutions?.join(', ') || 'Geen huidige oplossingen';
    processedHtml = processedHtml.replace(/{{currentSolutions}}/g, solutions);
    
    const expansionPlans = quickscanData.expansionPlans?.join(', ') || 'Geen uitbreidingsplannen';
    processedHtml = processedHtml.replace(/{{expansionPlans}}/g, expansionPlans);
    
    processedHtml = processedHtml.replace(/{{energyPotentialScore}}/g, results.energyPotentialScore?.toString() || '0');
    processedHtml = processedHtml.replace(/{{yearlyAnnualSavings}}/g, results.yearlyAnnualSavings?.toString() || '0');
    processedHtml = processedHtml.replace(/{{co2Reduction}}/g, results.co2Reduction?.toString() || '0');
    processedHtml = processedHtml.replace(/{{roiMonths}}/g, results.roiMonths?.toString() || '0');
    
    const formattedSavings = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(results.yearlyAnnualSavings || 0);
    processedHtml = processedHtml.replace(/{{formattedSavings}}/g, formattedSavings);
    
    const recommendations = results.recommendations?.map((rec: string, index: number) => 
      `<li>${rec}</li>`
    ).join('') || '<li>Geen aanbevelingen beschikbaar</li>';
    processedHtml = processedHtml.replace(/{{recommendations}}/g, recommendations);
    
    if (contactInfo) {
      processedHtml = processedHtml.replace(/{{companyName}}/g, contactInfo.companyName || 'Uw Bedrijf');
      processedHtml = processedHtml.replace(/{{email}}/g, contactInfo.email || '');
    }
    
    const now = new Date();
    const formattedDate = now.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    processedHtml = processedHtml.replace(/{{currentDate}}/g, formattedDate);
    processedHtml = processedHtml.replace(/{{submissionId}}/g, submissionId || '');
    
    // Write the processed HTML to the new window
    printWindow.document.write(processedHtml);
    printWindow.document.close();
    
    // Auto-trigger print dialog after a short delay
    setTimeout(() => {
      printWindow.print();
    }, 1000);
  };

  const generatePDF = async (
    htmlTemplate: string,
    quickscanData: QuickscanData,
    results: QuickscanResults,
    contactInfo?: {
      email?: string;
      companyName?: string;
    },
    submissionId?: string
  ) => {
    setIsGenerating(true);
    setGenerationError(null);
    
    try {
      console.log('Starting PDF generation...');
      console.log('Data being sent:', {
        htmlTemplateLength: htmlTemplate.length,
        quickscanData: quickscanData,
        results: results,
        contactInfo: contactInfo,
        submissionId: submissionId
      });
      
      const { data, error } = await supabase.functions.invoke('generate-pdf', {
        body: {
          htmlTemplate,
          quickscanData,
          results,
          contactInfo,
          submissionId
        }
      });

      if (error) {
        console.error('Error from PDF generation function:', error);
        console.log('Attempting client-side PDF generation as fallback...');
        
        // Try client-side generation as fallback
        generateClientSidePDF(htmlTemplate, quickscanData, results, contactInfo, submissionId);
        return true;
      }

      console.log('PDF generation response received');
      console.log('Response type:', typeof data);
      console.log('Response size:', data?.byteLength || data?.length || 'unknown');

      if (!data) {
        console.log('No PDF data received, trying client-side fallback...');
        generateClientSidePDF(htmlTemplate, quickscanData, results, contactInfo, submissionId);
        return true;
      }

      // Handle different response types
      let blob: Blob;
      if (data instanceof ArrayBuffer) {
        blob = new Blob([data], { type: 'application/pdf' });
      } else if (data instanceof Uint8Array) {
        blob = new Blob([new Uint8Array(data)], { type: 'application/pdf' });
      } else if (typeof data === 'string') {
        // If we get a string, convert it to blob
        blob = new Blob([data], { type: 'application/pdf' });
      } else {
        // Try to convert whatever we got
        blob = new Blob([data], { type: 'application/pdf' });
      }
      
      // Verify the blob has meaningful content
      if (blob.size === 0 || blob.size < 100) {
        console.log('PDF blob too small, trying client-side fallback...');
        generateClientSidePDF(htmlTemplate, quickscanData, results, contactInfo, submissionId);
        return true;
      }
      
      console.log('PDF blob created, size:', blob.size, 'bytes');
      
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `quickscan-rapport-${submissionId || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
      
      console.log('PDF download initiated successfully');
      return true;
      
    } catch (error) {
      console.error('PDF generation error:', error);
      console.log('Attempting client-side PDF generation as fallback...');
      
      try {
        generateClientSidePDF(htmlTemplate, quickscanData, results, contactInfo, submissionId);
        return true;
      } catch (fallbackError) {
        console.error('Client-side PDF generation also failed:', fallbackError);
        const errorMessage = fallbackError instanceof Error ? fallbackError.message : 'Er ging iets mis bij het genereren van de PDF';
        setGenerationError(errorMessage);
        throw new Error(errorMessage);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generatePDF,
    isGenerating,
    generationError
  };
};
